import React, { useState, useEffect } from "react";
import { Button, FormControl, MenuItem, Select, Checkbox } from "@mui/material";
import { BorderColorOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

function LetterTemplateForm({
  selectedTemplate,
  handleTemplateChange,
  template,
  userDetails,
  clinicDetails,
  clinic_image,
  onSelectedDataChange,
}) {
  const navigate = useNavigate();

  // Checkbox state for user and clinic details
  const [selectedUserDetails, setSelectedUserDetails] = useState({});
  const [selectedClinicDetails, setSelectedClinicDetails] = useState({});

  // Helper function to check if a value is not empty
  const isValueNotEmpty = (value) => {
    return value !== null && value !== undefined && value !== '' && String(value).trim() !== '';
  };

  // Initialize all checkboxes as selected when userDetails or clinicDetails change
  useEffect(() => {
    if (
      userDetails?.length > 0 &&
      Object.keys(selectedUserDetails).length === 0
    ) {
      const initialUserState = {};
      userDetails.forEach(detail => {
        if (isValueNotEmpty(detail.value)) {
          initialUserState[detail.label] = true;
        }
      });
      setSelectedUserDetails(initialUserState);
    }
    // Only set if state is empty!
  }, [userDetails]);

  useEffect(() => {
    if (
      clinicDetails?.length > 0 &&
      Object.keys(selectedClinicDetails).length === 0
    ) {
      const initialClinicState = {};
      clinicDetails.forEach(detail => {
        if (isValueNotEmpty(detail.value)) {
          initialClinicState[detail.label] = true;
        }
      });
      setSelectedClinicDetails(initialClinicState);
    }
    // Only set if state is empty!
  }, [clinicDetails]);

  // Effect to pass selected data to parent component
  useEffect(() => {
    const selectedUserData = userDetails?.filter(detail =>
      selectedUserDetails[detail.label] === true
    ) || [];
    const selectedClinicData = clinicDetails?.filter(detail =>
      selectedClinicDetails[detail.label] === true
    ) || [];

    if (onSelectedDataChange) {
      onSelectedDataChange({
        selectedUserData,
        selectedClinicData,
        hasUserSelections: selectedUserData.length > 0,
        hasClinicSelections: selectedClinicData.length > 0,
      });
    }
  }, [selectedUserDetails, selectedClinicDetails, userDetails, clinicDetails, onSelectedDataChange]);

  // User Details handlers
  // Only consider non-empty user details for "Select All"
  const nonEmptyUserDetails = userDetails?.filter(d => isValueNotEmpty(d.value)) || [];
  const isAllUserSelected = nonEmptyUserDetails.length > 0 &&
    nonEmptyUserDetails.every((d) => selectedUserDetails[d.label] === true);
  
  const handleUserCheckbox = (label) => {
    console.log(`Toggling user checkbox for: ${label}`); // Debug log
    setSelectedUserDetails((prev) => ({
      ...prev,
      [label]: !prev[label] // Simple toggle
    }));
  };
  
  const handleUserSelectAll = (event) => {
    const checked = event.target.checked;
    if (checked) {
      const updated = {};
      nonEmptyUserDetails.forEach((d) => {
        updated[d.label] = true;
      });
      setSelectedUserDetails(updated);
    } else {
      setSelectedUserDetails({});
    }
  };

  // Clinic Details handlers
  // Only consider non-empty clinic details for "Select All"
  const nonEmptyClinicDetails = clinicDetails?.filter(d => isValueNotEmpty(d.value)) || [];
  const isAllClinicSelected = nonEmptyClinicDetails.length > 0 &&
    nonEmptyClinicDetails.every((d) => selectedClinicDetails[d.label] === true);
  
  const handleClinicCheckbox = (label) => {
    console.log(`Toggling clinic checkbox for: ${label}`); // Debug log
    setSelectedClinicDetails((prev) => ({
      ...prev,
      [label]: !prev[label] // Simple toggle
    }));
  };
  
  const handleClinicSelectAll = (event) => {
    const checked = event.target.checked;
    if (checked) {
      const updated = {};
      nonEmptyClinicDetails.forEach((d) => {
        updated[d.label] = true;
      });
      setSelectedClinicDetails(updated);
    } else {
      setSelectedClinicDetails({});
    }
  };

  // Debug logs
  useEffect(() => {
    console.log("Selected User Details:", selectedUserDetails);
  }, [selectedUserDetails]);

  useEffect(() => {
    console.log("Selected Clinic Details:", selectedClinicDetails);
  }, [selectedClinicDetails]);

  return (
    <div
      className="w-full p-2 p-md-3 py-2 py-md-2"
      style={{ background: "#fff" }}
    >
      <div>
        {/* Select Letter Type */}
        <div
          style={{ background: "#F2F4F7", border: "1px solid #D8D8D8" }}
          className="rounded-3 p-4 mt-3 d-flex align-items-center flex-wrap justify-content-between"
        >
          <div>
            <h5 style={{ color: "#0C1C44" }}>Select Letter Type</h5>
            <p className="text-muted">
              Choose the kind of letter you want to send, and we will fill in a
              template for you.
            </p>
          </div>
          <FormControl>
            <Select
              value={selectedTemplate}
              onChange={handleTemplateChange}
              displayEmpty
              size="small"
              sx={{
                maxWidth: "300px !important",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D8D8D8",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0C1C44",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0C1C44 !important",
                },
              }}
            >
              <MenuItem value="" disabled>
                <p style={{ color: "#0C1C44", margin: 0 }}>
                  Select letter type
                </p>
              </MenuItem>
              {template
                ?.filter((item) => item?.is_active)
                ?.map((item, idx) => (
                  <MenuItem key={idx} value={item?.template_name}>
                    {item?.template_name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>

        {/* Review Personal Details */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #D8D8D8",
            borderRadius: "10px 10px 0px 0px",
          }}
          className="p-4 mt-3 d-flex align-items-center flex-wrap justify-content-between"
        >
          <div>
            <h5>Review Your Personal Details</h5>
            <p className="text-muted">
              The fields you select will be reflected in your letter. 
              If you want to modify them, click on the 'Edit' button to go to your Profile.
            </p>
          </div>
          <Button
            sx={{
              backgroundColor: "#F2F4F7",
              color: "#0C1C44",
              borderColor: "#0C1C44",
              "&:hover": {
                backgroundColor: "#F2F4F7",
                color: "#0C1C44",
                borderColor: "#0C1C44",
              },
              "&:focus": {
                backgroundColor: "#F2F4F7",
                color: "#0C1C44",
                borderColor: "#0C1C44",
              },
            }}
            className="p-3 rounded-4"
            variant="outlined"
            onClick={() => navigate("/clinical/settings")}
          >
            <BorderColorOutlined />
          </Button>
        </div>

        {/* User & Clinic Details */}
        <div
          style={{
            background: "#fff",
            borderWidth: "0px 1px 1px 1px",
            borderStyle: "solid",
            borderColor: "#D8D8D8",
            borderRadius: "0px 0px 10px 10px",
          }}
          className="p-4"
        >
          {/* User Details */}
          <div>
            <h6 className="text-muted">Your Details</h6>
            {/* <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <span style={{ fontWeight: 500 }}>Select All</span>
              <Checkbox
                checked={isAllUserSelected}
                onChange={(e) => handleUserSelectAll(e)}
                color="default"
                sx={{
                  color: "#D0D5DD",
                  "&.Mui-checked": { color: "#17B26A" },
                  "& .MuiSvgIcon-root": {
                    color: "#D0D5DD",
                    borderRadius: "5px",
                    fontSize: 24,
                  },
                  "&.Mui-checked .MuiSvgIcon-root": {
                    backgroundColor: "#17B26A33",
                    color: "#17B26A",
                  },
                }}
              />
            </div> */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "16px",
              }}
              className="mt-3"
            >
              {userDetails?.map((detail, index) => (
                <div key={index}>
                  <div>
                    <h6>{detail?.label}</h6>
                    <p
                      className="p-2 rounded-3 font-w500 d-flex justify-content-between align-items-center"
                      style={{
                          background: "#fff", 
                          color: "#212529bf",
                          minHeight: "40px",
                          border: "1px solid #D8D8D8",
                      }}
                    >
                      <span style={{ flex: 1, marginRight: "8px" }}>
                        {detail?.value || ""}
                      </span>
                      <Checkbox
                        checked={!!selectedUserDetails[detail.label]}
                        onChange={() => handleUserCheckbox(detail.label)}
                        disabled={!isValueNotEmpty(detail.value)}
                        color="default"
                        sx={{
                          color: "#D0D5DD",
                          "&.Mui-checked": { color: "#17B26A" },
                          "& .MuiSvgIcon-root": {
                            color: "#D0D5DD",
                            borderRadius: "5px",
                            fontSize: 24,
                          },
                          "&.Mui-checked .MuiSvgIcon-root": {
                            backgroundColor: "#17B26A33",
                            color: "#17B26A",
                          },
                          "&.Mui-disabled": {
                            color: "#E0E0E0",
                          },
                          flexShrink: 0,
                        }}
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Clinic Details */}
          <div className="mt-4">
            <h6 className="text-muted">Your Clinic's Details</h6>
            <div className="d-flex align-items-center flex-wrap gap-4 mt-3">
              <h6>Clinic's Logo</h6>
              <img
                src={clinic_image}
                width={50}
                height={50}
                alt=""
                loading="lazy"
                style={{
                  objectFit: "cover",
                  backgroundColor: "rgba(0,0,0,0.07)",
                }}
              />
            </div>
            {/* <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 8,
                marginTop: 16,
              }}
            >
              <span style={{ fontWeight: 500 }}>Select All</span>
              <Checkbox
                checked={isAllClinicSelected}
                onChange={(e) => handleClinicSelectAll(e)}
                color="default"
                sx={{
                  color: "#D0D5DD",
                  "&.Mui-checked": { color: "#17B26A" },
                  "& .MuiSvgIcon-root": {
                    color: "#D0D5DD",
                    borderRadius: "5px",
                    fontSize: 24,
                  },
                  "&.Mui-checked .MuiSvgIcon-root": {
                    backgroundColor: "#17B26A33",
                    color: "#17B26A",
                  },
                }}
              />
            </div> */}
            {/* Clinic Info Rows */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "16px",
              }}
              className="mt-3"
            >
              {clinicDetails.map((detail, index) => (
                <div key={index}>
                  <div>
                    <h6>{detail.label}</h6>
                    <p
                      className="p-2 rounded-3 font-w500 d-flex justify-content-between align-items-center"
                      style={{
                          background: "#fff", 
                          color: "#212529bf",
                          minHeight: "40px",
                          border: "1px solid #D8D8D8",
                      }}
                    >
                      <span>{detail.value}</span>
                      <Checkbox
                        checked={selectedClinicDetails[detail.label] === true}
                        onChange={() => handleClinicCheckbox(detail.label)}
                        color="default"
                        disabled={!isValueNotEmpty(detail.value)}
                        sx={{
                          color: "#D0D5DD",
                          "&.Mui-checked": { color: "#17B26A" },
                          "& .MuiSvgIcon-root": {
                            color: "#D0D5DD",
                            borderRadius: "5px",
                            fontSize: 24,
                          },
                          "&.Mui-checked .MuiSvgIcon-root": {
                            backgroundColor: "#17B26A33",
                            color: "#17B26A",
                          },
                          "&.Mui-disabled": {
                            color: "#E0E0E0",
                          },
                        }}
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </div>
  );

}

export default LetterTemplateForm;